import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { 
  Diamond, Coins, CreditCard, DollarSign, History, Gift, 
  TrendingUp, TrendingDown, Clock, ArrowRight, Sparkles, Crown, RefreshCw, Lock, Home, Trophy
} from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";
import HouseholdSection from "./HouseholdSection";
import DailyChallengesTab from "./DailyChallengesTab";

const MyAccountPanel = ({ onClose, initialTab = "overview" }) => {
  const { token, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [diamondBalance, setDiamondBalance] = useState(0);
  const [fchipsBalance, setFchipsBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [fchipsStats, setFchipsStats] = useState({ total_won: 0, total_lost: 0 });
  const [diamondStats, setDiamondStats] = useState({ total_purchased: 0, total_spent: 0, total_received: 0 });
  const [purchaseAmount, setPurchaseAmount] = useState(20);
  const [cashoutAmount, setCashoutAmount] = useState(1000);
  const [tipAmount, setTipAmount] = useState(100);
  const [tipRecipient, setTipRecipient] = useState("");
  const [tipMessage, setTipMessage] = useState("");
  const [fchipsTipAmount, setFchipsTipAmount] = useState(100);
  const [fchipsTipRecipient, setFchipsTipRecipient] = useState("");
  const [processing, setProcessing] = useState(false);
  const [claimingBonus, setClaimingBonus] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [diamondRes, fchipsRes, txRes] = await Promise.all([
        axios.get(`${API}/diamonds/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/fchips/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/transactions/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDiamondBalance(diamondRes.data.balance || 0);
      setDiamondStats({
        total_purchased: diamondRes.data.total_purchased || 0,
        total_spent: diamondRes.data.total_spent || 0,
        total_received: diamondRes.data.total_received || 0
      });
      
      setFchipsBalance(fchipsRes.data.balance || 0);
      setFchipsStats({
        total_won: fchipsRes.data.total_won || 0,
        total_lost: fchipsRes.data.total_lost || 0
      });
      
      setTransactions(txRes.data.transactions || []);
    } catch (error) {
      console.error("Failed to fetch account data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFchipsTip = async () => {
    if (!fchipsTipRecipient.trim()) {
      toast.error("Enter a recipient username");
      return;
    }
    if (fchipsTipAmount < 10) {
      toast.error("Minimum tip is 10 F-Chips");
      return;
    }
    if (fchipsTipAmount > fchipsBalance) {
      toast.error("Insufficient F-Chips");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/fchips/tip`,
        { to_username: fchipsTipRecipient, amount: fchipsTipAmount, message: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setFchipsTipRecipient("");
      setFchipsBalance(response.data.new_balance);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Tip failed");
    } finally {
      setProcessing(false);
    }
  };

  const handlePurchaseDiamonds = async () => {
    if (purchaseAmount < 20) {
      toast.error("Minimum purchase is $20");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/diamonds/purchase`,
        { amount_dollars: purchaseAmount, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { checkout_url, session_id } = response.data;
      if (!checkout_url) {
        toast.error("Payment could not be started");
        return;
      }
      try {
        sessionStorage.setItem("pending_checkout_session", session_id);
        sessionStorage.setItem("pending_checkout_kind", "diamond_purchase");
      } catch (_e) { /* noop */ }
      toast.info("Redirecting to Stripe Checkout…");
      window.location.href = checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
      setProcessing(false);
    }
  };

  const handleCashout = async () => {
    if (cashoutAmount < 1000) {
      toast.error("Minimum cashout is 1000 diamonds");
      return;
    }
    if (cashoutAmount > diamondBalance) {
      toast.error("Insufficient diamonds");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/diamonds/cashout`,
        { amount: cashoutAmount, method: "paypal", payout_method: "paypal" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Cashout requested");
      fetchData();
    } catch (error) {
      let msg = "Cashout failed";
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) msg = detail[0]?.msg || msg;
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleTip = async () => {
    if (!tipRecipient.trim()) {
      toast.error("Enter a recipient username");
      return;
    }
    if (tipAmount < 10) {
      toast.error("Minimum tip is 10 diamonds");
      return;
    }
    if (tipAmount > diamondBalance) {
      toast.error("Insufficient diamonds");
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/diamonds/tip`,
        { to_username: tipRecipient, amount: tipAmount, message: tipMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setTipRecipient("");
      setTipMessage("");
      fetchData();
    } catch (error) {
      let msg = "Tip failed";
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) msg = detail[0]?.msg || msg;
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleClaimDailyBonus = async () => {
    setClaimingBonus(true);
    try {
      const response = await axios.post(`${API}/fchips/daily-bonus`, {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setFchipsBalance(response.data.new_balance);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not claim bonus");
    } finally {
      setClaimingBonus(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const getTransactionIcon = (type) => {
    switch(type) {
      case 'purchase': return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'tip_sent': return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'tip_received': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'cashout': return <DollarSign className="w-4 h-4 text-orange-500" />;
      case 'vip_purchase': return <Crown className="w-4 h-4 text-yellow-500" />;
      default: return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTransactionColor = (type, amount) => {
    if (type === 'tip_received' || type === 'purchase') return 'text-green-600';
    if (type === 'tip_sent' || type === 'cashout' || type === 'vip_purchase') return 'text-red-600';
    return amount > 0 ? 'text-green-600' : 'text-red-600';
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const isGoldUser = diamondBalance >= 2000;

  return (
    <div className="fixed inset-0 bg-black/30 z-[9999]" data-testid="my-account-panel">
      <DraggableResizableWindow
        title={<><Diamond className="w-4 h-4 inline mr-2" />My Account</>}
        onClose={onClose}
        initialPosition={{ x: Math.max(50, (window.innerWidth - 600) / 2), y: 80 }}
        initialSize={{ width: 600, height: 550 }}
        minSize={{ width: 500, height: 400 }}
        maxSize={{ width: 800, height: 700 }}
        zIndex={9999}
        titleBarColor="bg-gradient-to-r from-[#FFD700] to-[#FFA500]"
      >
        <div className="p-3 h-full flex flex-col overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#FFD700] border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Compact User Info + Balances strip — fits all key info in 1/3 the vertical space */}
              <div className="retro-window mb-3 p-2 bg-gradient-to-r from-[#FFF9D9] via-white to-[#E8F4FA]" data-testid="account-summary-strip">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {/* Username + email */}
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div
                      className="w-8 h-8 rounded-sm flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ backgroundColor: user?.avatar_color || "#7B0099" }}
                    >
                      {user?.username?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-verdana text-sm font-bold flex items-center gap-1 truncate" data-testid="account-username">
                        {user?.username}
                        {isGoldUser && <Crown className="w-3 h-3 text-yellow-600" title="Gold User" />}
                      </p>
                      <p className="font-arial text-[10px] text-[#666] truncate" data-testid="account-email">
                        {user?.email || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Compact balance pills */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div
                      className={`flex items-center gap-1 px-2 py-1 rounded border ${isGoldUser ? 'bg-yellow-100 border-yellow-500' : 'bg-cyan-50 border-cyan-300'}`}
                      title="Diamonds — real value"
                      data-testid="diamonds-pill"
                    >
                      <Diamond className="w-4 h-4 text-cyan-600" />
                      <span className="font-bold text-sm text-cyan-700">{diamondBalance.toLocaleString()}</span>
                    </div>
                    <div
                      className="flex items-center gap-1 px-2 py-1 rounded border bg-green-50 border-green-300"
                      title="F-Chips — play money (games only)"
                      data-testid="fchips-pill"
                    >
                      <Coins className="w-4 h-4 text-green-600" />
                      <span className="font-bold text-sm text-green-700">{fchipsBalance.toLocaleString()}</span>
                    </div>
                    <button
                      className="retro-button text-[10px] px-2 py-1 bg-yellow-100 flex items-center gap-1 flex-shrink-0"
                      onClick={handleClaimDailyBonus}
                      disabled={claimingBonus}
                      title="Claim Daily Bonus"
                      data-testid="claim-daily-bonus-btn"
                    >
                      {claimingBonus ? "..." : <><Gift className="w-3 h-3" /> Daily</>}
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b-2 border-[#808080] mb-2 flex-wrap">
                {[
                  { id: 'overview', label: 'Overview', icon: <Sparkles className="w-3 h-3" /> },
                  { id: 'challenges', label: 'Challenges', icon: <Trophy className="w-3 h-3" /> },
                  { id: 'transactions', label: 'History', icon: <History className="w-3 h-3" /> },
                  { id: 'buy', label: 'Buy', icon: <CreditCard className="w-3 h-3" /> },
                  { id: 'cashout', label: 'Cash Out', icon: <DollarSign className="w-3 h-3" /> },
                  { id: 'tip', label: 'Tip', icon: <Gift className="w-3 h-3" /> },
                  { id: 'family', label: 'Family', icon: <Home className="w-3 h-3" /> },
                  { id: 'security', label: 'Security', icon: <Lock className="w-3 h-3" /> },
                ].map(tab => (
                  <button
                    key={tab.id}
                    className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${
                      activeTab === tab.id
                        ? 'bg-[#FFCC00] text-[#333] border-t-2 border-l-2 border-r-2 border-[#808080] -mb-[2px]'
                        : 'bg-[#C0C0C0] text-[#333] hover:bg-[#E0E0E0]'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`account-tab-${tab.id}`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="flex-1 retro-inset p-3 overflow-y-auto retro-scrollbar">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {/* Diamond Stats */}
                      <div className="retro-window p-3">
                        <h4 className="font-bold text-xs mb-2 text-cyan-700 flex items-center gap-1">
                          <Diamond className="w-4 h-4" /> Diamond Stats
                        </h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Total Purchased:</span>
                            <span className="text-green-600 font-bold">+{diamondStats.total_purchased.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Total Spent:</span>
                            <span className="text-red-600 font-bold">-{diamondStats.total_spent.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Tips Received:</span>
                            <span className="text-green-600 font-bold">+{diamondStats.total_received.toLocaleString()}</span>
                          </div>
                          <hr className="my-1" />
                          <div className="flex justify-between font-bold">
                            <span>Current Balance:</span>
                            <span className="text-cyan-700">{diamondBalance.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* F-Chips Stats */}
                      <div className="retro-window p-3">
                        <h4 className="font-bold text-xs mb-2 text-green-700 flex items-center gap-1">
                          <Coins className="w-4 h-4" /> F-Chips Stats
                        </h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Total Won:</span>
                            <span className="text-green-600 font-bold">+{fchipsStats.total_won.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Total Lost:</span>
                            <span className="text-red-600 font-bold">-{fchipsStats.total_lost.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Net Profit/Loss:</span>
                            <span className={fchipsStats.total_won - fchipsStats.total_lost >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                              {fchipsStats.total_won - fchipsStats.total_lost >= 0 ? '+' : ''}{(fchipsStats.total_won - fchipsStats.total_lost).toLocaleString()}
                            </span>
                          </div>
                          <hr className="my-1" />
                          <div className="flex justify-between font-bold">
                            <span>Current Balance:</span>
                            <span className="text-green-700">{fchipsBalance.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Info */}
                    <div className="retro-window p-3 bg-blue-50">
                      <h4 className="font-bold text-xs mb-2">💡 Quick Info</h4>
                      <ul className="text-xs space-y-1 text-gray-700">
                        <li>• <strong>Diamonds</strong> are our premium currency ($1 = 100 💎)</li>
                        <li>• <strong>F-Chips</strong> are play money for games only - no cash value</li>
                        <li>• Reach 2000+ diamonds to become a <strong className="text-yellow-600">Gold User</strong>!</li>
                        <li>• Claim your <strong>daily F-Chips bonus</strong> for free chips every day</li>
                        <li>• Cash out diamonds anytime (15% fee, min 1000 diamonds)</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-bold text-xs">Transaction History</h4>
                      <button className="retro-button text-xs px-2 py-0.5" onClick={fetchData}>
                        <RefreshCw className="w-3 h-3 inline" /> Refresh
                      </button>
                    </div>
                    {transactions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No transactions yet</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {transactions.map((tx, i) => (
                          <div key={i} className="retro-window p-2 flex items-center gap-2" data-testid={`tx-row-${i}`}>
                            {getTransactionIcon(tx.type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold truncate">
                                {tx.description || tx.type}
                                {tx.counterparty && <span className="text-gray-500 font-normal"> · {tx.counterparty}</span>}
                              </p>
                              <p className="text-[10px] text-gray-500">{formatDate(tx.timestamp)}</p>
                            </div>
                            <span className={`font-bold text-sm ${getTransactionColor(tx.type, tx.amount)}`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount?.toLocaleString()}
                              {tx.currency === 'fchips' ? ' 🎰' : ' 💎'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Buy Diamonds Tab */}
                {activeTab === 'buy' && (
                  <div className="space-y-4">
                    <div className="retro-window p-3 bg-green-50">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-green-600" /> Buy Diamonds
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">$1 = 100 Diamonds | Minimum purchase: $20</p>
                      
                      {/* Popular Packages */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {[
                          { amt: 20, bonus: 0, label: "Starter" },
                          { amt: 50, bonus: 500, label: "Popular" },
                          { amt: 100, bonus: 1500, label: "Best Value" },
                          { amt: 200, bonus: 4000, label: "VIP Pack" }
                        ].map(pkg => (
                          <button
                            key={pkg.amt}
                            className={`retro-button p-2.5 text-center relative ${purchaseAmount === pkg.amt ? 'bg-green-200 border-green-500 ring-2 ring-green-400' : 'hover:bg-green-50'}`}
                            onClick={() => setPurchaseAmount(pkg.amt)}
                          >
                            {pkg.bonus > 0 && (
                              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                                +{pkg.bonus} BONUS
                              </span>
                            )}
                            <p className="text-[10px] text-gray-500 mb-1">{pkg.label}</p>
                            <p className="font-bold text-lg">${pkg.amt}</p>
                            <p className="text-xs text-cyan-600 font-bold">{((pkg.amt * 100) + pkg.bonus).toLocaleString()} 💎</p>
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold">Custom: $</span>
                        <input
                          type="number"
                          min="20"
                          value={purchaseAmount}
                          onChange={(e) => setPurchaseAmount(Math.max(20, parseInt(e.target.value) || 20))}
                          className="retro-input w-24 text-sm text-center font-bold"
                          data-testid="diamond-purchase-amount-input"
                        />
                        <span className="text-sm text-gray-600">= <strong className="text-cyan-600">{(purchaseAmount * 100).toLocaleString()}</strong> 💎</span>
                      </div>

                      {/* Summary Box */}
                      <div className="retro-inset p-2.5 mb-3 bg-white">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Base Diamonds:</span>
                          <span className="font-bold">{(purchaseAmount * 100).toLocaleString()} 💎</span>
                        </div>
                        {purchaseAmount >= 50 && (
                          <div className="flex justify-between text-xs text-green-600 mb-1">
                            <span>Bonus Diamonds:</span>
                            <span className="font-bold">
                              +{purchaseAmount >= 200 ? '4,000' : purchaseAmount >= 100 ? '1,500' : '500'} 💎
                            </span>
                          </div>
                        )}
                        <hr className="my-1.5" />
                        <div className="flex justify-between font-bold">
                          <span>Total Diamonds:</span>
                          <span className="text-cyan-600 text-sm">
                            {(purchaseAmount * 100 + (purchaseAmount >= 200 ? 4000 : purchaseAmount >= 100 ? 1500 : purchaseAmount >= 50 ? 500 : 0)).toLocaleString()} 💎
                          </span>
                        </div>
                      </div>

                      <button 
                        className="retro-button w-full py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold flex items-center justify-center gap-2 shadow-md hover:from-green-400 hover:to-emerald-400 transition-all"
                        onClick={handlePurchaseDiamonds}
                        disabled={processing}
                        data-testid="buy-diamonds-btn"
                      >
                        {processing ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4" /> 
                            Pay ${purchaseAmount} with Stripe
                          </>
                        )}
                      </button>
                      
                      {/* Security Note */}
                      <div className="flex items-center justify-center gap-2 mt-3 text-[10px] text-gray-500">
                        <span>🔒 Secure Payment</span>
                        <span>|</span>
                        <span>💳 Visa/Mastercard/Amex</span>
                        <span>|</span>
                        <span>Powered by Stripe</span>
                      </div>
                    </div>
                    
                    {/* Gold User Promo */}
                    {!isGoldUser && (
                      <div className="retro-window p-3 bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-400">
                        <div className="flex items-center gap-2">
                          <Crown className="w-6 h-6 text-yellow-600" />
                          <div>
                            <p className="font-bold text-sm text-yellow-800">Become a Gold User!</p>
                            <p className="text-xs text-yellow-700">
                              Maintain 2,000+ diamonds to unlock Gold User status and exclusive perks
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Cash Out Tab */}
                {activeTab === 'cashout' && (
                  <div className="space-y-4">
                    <div className="retro-window p-3 bg-orange-50">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-orange-600" /> Cash Out Diamonds
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">Minimum: 1000 diamonds | 15% processing fee</p>
                      
                      <div className="retro-inset p-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span>Available Balance:</span>
                          <span className="font-bold text-cyan-700">{diamondBalance.toLocaleString()} 💎</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm">Amount:</span>
                        <input
                          type="number"
                          min="1000"
                          max={diamondBalance}
                          value={cashoutAmount}
                          onChange={(e) => setCashoutAmount(Math.min(diamondBalance, Math.max(1000, parseInt(e.target.value) || 1000)))}
                          className="retro-input flex-1 text-sm"
                        />
                        <span className="text-sm">💎</span>
                      </div>

                      <div className="retro-inset p-2 mb-3 text-xs">
                        <div className="flex justify-between mb-1">
                          <span>Cashout Amount:</span>
                          <span>{cashoutAmount.toLocaleString()} 💎</span>
                        </div>
                        <div className="flex justify-between mb-1 text-red-600">
                          <span>Processing Fee (15%):</span>
                          <span>-{Math.floor(cashoutAmount * 0.15).toLocaleString()} 💎</span>
                        </div>
                        <hr className="my-1" />
                        <div className="flex justify-between font-bold">
                          <span>You'll Receive:</span>
                          <span className="text-green-600">${((cashoutAmount - Math.floor(cashoutAmount * 0.15)) / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      <button 
                        className="retro-button w-full py-2 bg-orange-500 text-white font-bold flex items-center justify-center gap-2"
                        onClick={handleCashout}
                        disabled={processing || cashoutAmount > diamondBalance || cashoutAmount < 1000}
                      >
                        <DollarSign className="w-4 h-4" /> 
                        {processing ? "Processing..." : `Cash Out $${((cashoutAmount - Math.floor(cashoutAmount * 0.15)) / 100).toFixed(2)}`}
                      </button>
                      <p className="text-[10px] text-gray-500 text-center mt-2">* Payouts via PayPal within 3-5 business days</p>
                    </div>
                  </div>
                )}

                {/* Send Tip Tab */}
                {activeTab === 'tip' && (
                  <div className="space-y-4">
                    {/* Diamond Tip Section */}
                    <div className="retro-window p-3 bg-pink-50">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <Gift className="w-5 h-5 text-pink-600" /> Send Diamond Tip 💎
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">Show appreciation by sending diamonds to another user!</p>
                      
                      <div className="retro-inset p-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span>Your Balance:</span>
                          <span className="font-bold text-cyan-700">{diamondBalance.toLocaleString()} 💎</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div>
                          <label className="text-xs font-bold">Recipient Username:</label>
                          <input
                            type="text"
                            value={tipRecipient}
                            onChange={(e) => setTipRecipient(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Enter username"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold">Amount (min 10):</label>
                          <input
                            type="number"
                            min="10"
                            max={diamondBalance}
                            value={tipAmount}
                            onChange={(e) => setTipAmount(Math.min(diamondBalance, Math.max(10, parseInt(e.target.value) || 10)))}
                            className="retro-input w-full text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold">Message (optional):</label>
                          <input
                            type="text"
                            value={tipMessage}
                            onChange={(e) => setTipMessage(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Say something nice..."
                            maxLength={100}
                          />
                        </div>
                      </div>

                      <button 
                        className="retro-button w-full py-2 bg-pink-500 text-white font-bold flex items-center justify-center gap-2"
                        onClick={handleTip}
                        disabled={processing || !tipRecipient.trim() || tipAmount > diamondBalance || tipAmount < 10}
                      >
                        <Gift className="w-4 h-4" /> 
                        {processing ? "Sending..." : `Send ${tipAmount.toLocaleString()} 💎 to ${tipRecipient || '...'}`}
                      </button>
                    </div>

                    {/* F-Chips Tip Section */}
                    <div className="retro-window p-3 bg-green-50">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <Coins className="w-5 h-5 text-green-600" /> Send F-Chips 🎰
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">Send play money to your friends for games!</p>
                      
                      <div className="retro-inset p-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span>Your F-Chips:</span>
                          <span className="font-bold text-green-700">{fchipsBalance.toLocaleString()} 🎰</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div>
                          <label className="text-xs font-bold">Recipient Username:</label>
                          <input
                            type="text"
                            value={fchipsTipRecipient}
                            onChange={(e) => setFchipsTipRecipient(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Enter username"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold">Amount (min 10):</label>
                          <input
                            type="number"
                            min="10"
                            max={fchipsBalance}
                            value={fchipsTipAmount}
                            onChange={(e) => setFchipsTipAmount(Math.min(fchipsBalance, Math.max(10, parseInt(e.target.value) || 10)))}
                            className="retro-input w-full text-sm"
                          />
                        </div>
                      </div>

                      <button 
                        className="retro-button w-full py-2 bg-green-500 text-white font-bold flex items-center justify-center gap-2"
                        onClick={handleFchipsTip}
                        disabled={processing || !fchipsTipRecipient.trim() || fchipsTipAmount > fchipsBalance || fchipsTipAmount < 10}
                      >
                        <Coins className="w-4 h-4" /> 
                        {processing ? "Sending..." : `Send ${fchipsTipAmount.toLocaleString()} F-Chips to ${fchipsTipRecipient || '...'}`}
                      </button>
                    </div>

                    {/* Future Note */}
                    <div className="text-[10px] text-gray-400 text-center mt-2">
                      💡 Coming soon: Buy & cash out F-Chips for real money!
                    </div>
                  </div>
                )}

                {/* Family / Household Tab */}
                {activeTab === 'family' && (
                  <HouseholdSection />
                )}

                {/* Daily Challenges Tab */}
                {activeTab === 'challenges' && (
                  <DailyChallengesTab />
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="space-y-4">
                    <div className="retro-window p-4">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Change Password
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-bold block mb-1">Current Password:</label>
                          <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Enter current password"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold block mb-1">New Password:</label>
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Enter new password (min 8 characters)"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold block mb-1">Confirm New Password:</label>
                          <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="retro-input w-full text-sm"
                            placeholder="Confirm new password"
                          />
                        </div>
                        <button 
                          className="retro-button w-full py-2 bg-blue-500 text-white font-bold flex items-center justify-center gap-2"
                          onClick={handlePasswordChange}
                          disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                        >
                          <Lock className="w-4 h-4" /> 
                          {changingPassword ? "Changing..." : "Change Password"}
                        </button>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 bg-yellow-50 p-3 rounded border border-yellow-200">
                      <p className="font-bold text-yellow-700 mb-1">🔒 Password Requirements:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Minimum 8 characters</li>
                        <li>We recommend using a mix of letters, numbers, and symbols</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DraggableResizableWindow>
    </div>
  );
};

export default MyAccountPanel;
